import React, {
  ReactElement,
  createRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";
import cx from "classnames";

type PropsArg = {
  show: boolean;
  title?: string;
  children: ReactElement;
  footer?: ReactElement;
  onClose: () => void;
  onSubmit?: () => void;
  className?: string;
  hideHeader?: boolean;
};

const modalRoot = document.getElementById("modal-root");

export function Portal({ children }: { children: ReactElement }) {
  const [elem, setElem] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const node = document.createElement("div");
    modalRoot?.appendChild(node);
    setElem(node);
    return () => {
      modalRoot?.removeChild(node);
    };
  }, []);

  if (!elem) {
    return null;
  }

  return createPortal(children, elem);
}

export function Modal(props: PropsArg) {
  return (
    <Portal>
      <ModalChild {...props} />
    </Portal>
  );
}

function ModalChild({
  show,
  title,
  children,
  footer,
  onClose,
  onSubmit,
  className,
  hideHeader,
}: PropsArg) {
  const modalRef = createRef<HTMLInputElement>();

  const close = useCallback(() => {
    if (modalRef.current) modalRef.current.checked = false;
    onClose();
  }, [modalRef, onClose]);

  useEffect(() => {
    if (modalRef.current) modalRef.current.checked = show;
  }, [show, modalRef]);

  return (
    <>
      <input
        ref={modalRef}
        type="checkbox"
        id="post-modal"
        className="modal-toggle"
      />

      <div className="modal">
        <div
          className={cx(
            "modal-box p-0 bg-base-300 w-11/12 max-w-2xl lg:max-w-7xl max-h-[95vh] relative overflow-y-auto",
            className
          )}
        >
          <button
            onClick={close}
            className="btn btn-sm btn-circle btn-ghost absolute right-3 top-3"
          >
            ✕
          </button>
          <FormWrapper onSubmit={onSubmit}>
            <>
              <div className="p-8">
                {!hideHeader && title && (
                  <div className="prose mb-6">
                    <h1>{title}</h1>
                  </div>
                )}

                {children}
              </div>

              {footer && (
                <div className="modal-action flex flex-col sm:flex-row md:sticky bottom-0 bg-base-300 p-8 shadow-2xl">
                  {footer}
                </div>
              )}
            </>
          </FormWrapper>
        </div>
      </div>
    </>
  );
}

const FormWrapper = ({
  onSubmit,
  children,
}: {
  onSubmit?: () => void;
  children: ReactElement;
}) => {
  if (onSubmit) {
    return <form onSubmit={onSubmit}>{children}</form>;
  }
  return children;
};
